import "@babel/polyfill";
import "mutationobserver-shim";
import { createApp, h } from "vue";
import Vue3TouchEvents from "vue3-touch-events";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import {
  abTestService,
  AbTest3300,
  AbTest2807,
  AbTest3113,
  AbTest3401
} from "@/app/services/AbTestService";
import Loading from "@/components/organisms/common/Loading.vue";
import BForm from "@/components/atoms/bootstrap/BForm.vue";
// NOTE: plugins 以下から import するのがベストプラクティスなのか悩ましいが、一旦既存の構成に合わせておく
import { BootstrapVue } from "@/plugins/bootstrapVue";
import { LoadingManager } from "@/plugins/loading";
import {
  ValidationField,
  ValidationForm,
  ValidationObserver,
  ValidationProvider,
  CustomValidationObserver
} from "@/plugins/veeValidate/veeValidate";
import TextareaAutosize from "vue-textarea-autosize";
import { components } from "@/plugins/svgComponents";
import { captureException, initSentry } from "@/plugins/sentry";
import { errorHandler } from "@/plugins/errorHandler";
import "@/plugins/adjust";
import { init } from "@/utils/image";

import "@/assets/scss/custom.scss";
import { applicationStore } from "./store/application";
import { masterStore } from "./store/master";
import { orderStore } from "./store/order";
import { authStore } from "./store/auth";
import {
  extractPathFromSchemeUrl,
  fixCordovaKeyboard
} from "./utils/browserUtil";
import "@/config/VueAppEnv"; // アプリのエントリポイントで必ずENVのチェックをするためのimport
import { initFirebasePlugin } from "./plugins/firebase";

const onDeviceReady = async () => {
  await init();
  fixCordovaKeyboard();

  // 一番最初にセッションIDを生成。保存されていなければ作成される
  authStore.generateSessionId();

  // 応募ボタン改善_第4弾の AB テスト
  try {
    const segmentNumber3300 = await abTestService.getSegmentNumber(
      AbTest3300.testKey,
      authStore.sessionId!
    );
    authStore.setSegmentNumber3300(segmentNumber3300);
    // entryの発火は案件詳細時に変更
  } catch (e) {
    // アプリの挙動には影響ないため、エラー通知はするが処理は続行する
    captureException(e as Error);
  }

  // 仕事詳細ページへの募集人数の表示の AB テスト
  try {
    const segmentNumber2807 = await abTestService.getSegmentNumber(
      AbTest2807.testKey,
      authStore.sessionId!
    );
    authStore.setSegmentNumber2807(segmentNumber2807);
  } catch (e) {
    // アプリの挙動には影響ないため、エラー通知はするが処理は続行する
    captureException(e as Error);
  }

  // オンボーディング画面改善（画面1枚に変更）の AB テスト
  try {
    const segmentNumber3113 = await abTestService.getSegmentNumber(
      AbTest3113.testKey,
      authStore.sessionId!
    );
    authStore.setSegmentNumber3113(segmentNumber3113);
  } catch (e) {
    // アプリの挙動には影響ないため、エラー通知はするが処理は続行する
    captureException(e as Error);
  }

  // 申込条件確認（応募チェック）画面改善の AB テスト
  try {
    const segmentNumber3401 = await abTestService.getSegmentNumber(
      AbTest3401.testKey,
      authStore.sessionId!
    );
    authStore.setSegmentNumber3401(segmentNumber3401);
  } catch (e) {
    // アプリの挙動には影響ないため、エラー通知はするが処理は続行する
    captureException(e as Error);
  }

  // ネイティブ UA から現在のプロダクトを authStore にセットする
  // （APIリクエストのヘッダーにここで設定したプロダクトをセットするため、APIリクエストの前に実行しておく）
  authStore.initializeCurrentProduct();

  // masterを取得してから起動
  await masterStore.fetchMaster();

  // 起動時には古いOrderList/EvalutionListを削除する
  orderStore.clearOrderList();
  orderStore.clearEvaluations();
  // あとで見るリストをセットアップ
  orderStore.setUpWatchLaterOrderList();
  // 応募データの取得中フラグを折る
  applicationStore.resetIsFetchingApplications();
  const app = createApp(App)
    .use(router)
    .use(store)
    .use(TextareaAutosize)
    .use(Vue3TouchEvents)
    .use(BootstrapVue)
    .use(components)
    .component("ValidationField", ValidationField)
    .component("ValidationForm", ValidationForm)
    .component("ValidationObserver", ValidationObserver)
    .component("ValidationProvider", ValidationProvider)
    .component("CustomValidationObserver", CustomValidationObserver)
    .component("BForm", BForm)
    .use({
      install: app => {
        const node = document.createElement("div");
        document.body.appendChild(node);

        const loading = createApp({
          components: {
            Loading
          },
          render: () => h(Loading, { ref: "loading" })
        }).mount(node);
        app.config.globalProperties.$loading = new LoadingManager(loading);
      }
    });

  app.config.errorHandler = errorHandler;

  initSentry(app);
  initFirebasePlugin();

  app.mount("#app");
};

/**
 * javascriptからcordovaを通してネイティブの機能へアクセスをするためには
 * `deviceready`イベントが発火している必要がある。そのため、それが完了するまで待つ
 * https://cordova.apache.org/docs/en/9.x/cordova/events/events.html#deviceready
 */
document.addEventListener("deviceready", onDeviceReady, false);

// cordovaを経由しない開発時のみ、擬似的に発火させる
if (navigator.userAgent.indexOf("CaisukeAPP") === -1) {
  onDeviceReady();
}

/**
 * custom url scheme対応
 * @param url `caisukew://aaa/bbb/ccc?ddd=eee&fff=ggg`というようなフォーマット
 */
// eslint-disable-next-line
// @ts-ignore
window.handleOpenURL = (url: string) => {
  const path = extractPathFromSchemeUrl(url);
  if (path === "") {
    return;
  }

  setTimeout(async () => {
    await router.push(path);
  }, 0);
};
